<template>
  <div class="refund-step-confirm">
    <div class="step-description">
      <p>{{ t('Revise os dados do estorno antes de confirmar') }}</p>
    </div>

    <!-- Warning Banner -->
    <div class="warning-banner">
      <i class="pi pi-exclamation-triangle"></i>
      <div class="warning-content">
        <strong>{{ t('Atenção') }}:</strong>
        {{ t('Esta ação não pode ser desfeita. O valor será devolvido ao cliente.') }}
      </div>
    </div>

    <!-- Refund Summary -->
    <div class="refund-summary">
      <div class="summary-header">
        <i class="pi pi-replay"></i>
        <span>{{ t('Resumo do Estorno') }}</span>
      </div>

      <div class="summary-content">
        <!-- Original Amount -->
        <div class="summary-row">
          <span class="summary-label">{{ t('Valor original') }}:</span>
          <span class="summary-value">{{ formatCurrency(originalAmountDisplay) }}</span>
        </div>

        <!-- Refund Amount -->
        <div class="summary-row highlight">
          <span class="summary-label">{{ t('Valor a estornar') }}:</span>
          <span class="summary-value refund-value">{{ formatCurrency(refundAmountDisplay) }}</span>
        </div>

        <!-- Refund Type -->
        <div class="summary-row">
          <span class="summary-label">{{ t('Tipo de estorno') }}:</span>
          <span class="summary-value">
            <Tag :severity="isFullRefund ? 'warn' : 'info'">
              {{ isFullRefund ? t('Estorno total') : t('Estorno parcial') }}
            </Tag>
          </span>
        </div>

        <!-- Reason (if provided) -->
        <div class="summary-row" v-if="modelValue.description">
          <span class="summary-label">{{ t('Motivo') }}:</span>
          <span class="summary-value reason">{{ modelValue.description }}</span>
        </div>

        <!-- Charge ID -->
        <div class="summary-row">
          <span class="summary-label">{{ t('ID da cobrança') }}:</span>
          <span class="summary-value code">{{ recebimento.recebimento_id_externo }}</span>
        </div>

        <!-- Provider -->
        <div class="summary-row" v-if="recebimento.recebimento_provedor">
          <span class="summary-label">{{ t('Provedor') }}:</span>
          <span class="summary-value">{{ recebimento.recebimento_provedor.toUpperCase() }}</span>
        </div>
      </div>
    </div>

    <!-- Confirmation Toggle -->
    <div class="confirmation-checkbox">
      <toggle
        data-testid="recebimento-refund-confirm-toggle"
        elem-id="refund_confirm"
        v-model="confirmed">
      </toggle>
      <label for="refund_confirm" class="confirm-label">
        {{ t('Confirmo que desejo estornar este pagamento') }}
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { permissionsState } from '@/services/permissionsService';
import { currencyMinorUnitsFactor, LOCALE_TO_CURRENCY } from '@/utils/currencyMinorUnits';

interface RefundModel {
  amount?: number;
  description?: string;
  confirmed?: boolean;
  [key: string]: unknown;
}

interface RecebimentoSource {
  recebimento_valor?: number;
  recebimento_id_externo?: string;
  recebimento_provedor?: string;
  [key: string]: unknown;
}

const { t } = useI18n();

const props = defineProps<{
  modelValue: RefundModel;
  recebimento: RecebimentoSource;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: RefundModel];
}>();

// Confirmation state - synced with parent via v-model
const confirmed = computed<boolean>({
  get: () => props.modelValue.confirmed || false,
  set: (value: boolean) => {
    emit('update:modelValue', {
      ...props.modelValue,
      confirmed: value
    });
  }
});

// Price factor derived from active locale (100 for BRL, 1 for CLP, etc.)
const priceFactor = currencyMinorUnitsFactor((permissionsState as { normalizedLocale?: string })?.normalizedLocale);


// Computed values
const originalAmountDisplay = computed<number>(() => {
  return (props.recebimento.recebimento_valor || 0) / priceFactor;
});

const refundAmountDisplay = computed<number>(() => {
  return (props.modelValue.amount || 0) / priceFactor;
});

const isFullRefund = computed<boolean>(() => {
  return props.modelValue.amount === props.recebimento.recebimento_valor;
});

// Validation - user must confirm
const isValid = computed<boolean>(() => {
  return confirmed.value && (props.modelValue.amount ?? 0) > 0;
});

// Methods
const formatCurrency = (value: number | undefined): string => {
  const locale = (permissionsState as { normalizedLocale?: string })?.normalizedLocale || 'pt-BR';
  const currency = LOCALE_TO_CURRENCY[locale] || 'BRL';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency
  }).format(value || 0);
};

// Reset confirmation when step becomes visible
const resetConfirmation = (): void => {
  confirmed.value = false;
};

// Expose for wizard
defineExpose({
  isValid,
  resetConfirmation
});
</script>

<style scoped>
.refund-step-confirm {
  padding: 1rem 0;
}

.step-description {
  margin-bottom: 1.5rem;
  color: var(--text-color-secondary);
}

.step-description p {
  margin: 0;
}

.warning-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background-color: var(--yellow-50);
  border: 1px solid var(--yellow-200);
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.warning-banner i {
  color: var(--yellow-600);
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.warning-content {
  color: var(--yellow-800);
  font-size: 0.875rem;
  line-height: 1.5;
}

.warning-content strong {
  color: var(--yellow-900);
}

.refund-summary {
  background-color: var(--surface-ground);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 1.5rem;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background-color: var(--surface-200);
  font-weight: 600;
  color: var(--text-color);
}

.summary-header i {
  color: var(--yellow-600);
}

.summary-content {
  padding: 1rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--surface-200);
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-row.highlight {
  background-color: var(--yellow-50);
  margin: 0 -1rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
}

.summary-label {
  color: var(--text-color-secondary);
  font-weight: 500;
}

.summary-value {
  font-weight: 600;
  color: var(--text-color);
  text-align: right;
}

.summary-value.refund-value {
  font-size: 1.125rem;
  color: var(--yellow-700);
}

.summary-value.code {
  font-family: monospace;
  font-size: 0.875rem;
  background-color: var(--surface-200);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.summary-value.reason {
  max-width: 60%;
  word-wrap: break-word;
  font-style: italic;
  color: var(--text-color-secondary);
}

.confirmation-checkbox {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background-color: var(--surface-ground);
  border-radius: 8px;
  border: 2px solid var(--surface-300);
}

.confirm-label {
  cursor: pointer;
  font-weight: 500;
  color: var(--text-color);
  user-select: none;
}
</style>
